import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { adminGetSystems, adminCreateSystem, adminUpdateSystem, adminDeleteSystem } from '@/lib/api';
import { toast } from 'sonner';

const AdminSystems = () => {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    consumer_label: '',
    description: ''
  });

  useEffect(() => {
    loadSystems();
  }, []);

  const loadSystems = async () => {
    try {
      const data = await adminGetSystems();
      setSystems(data || []);
    } catch (error) {
      toast.error('Failed to load systems');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingSystem(null);
    setFormData({ name: '', consumer_label: '', description: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (system) => {
    setEditingSystem(system);
    setFormData({
      name: system.name,
      consumer_label: system.consumer_label,
      description: system.description || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSystem) {
        await adminUpdateSystem(editingSystem.id, formData);
        toast.success('System updated');
      } else {
        await adminCreateSystem(formData);
        toast.success('System created');
      }
      setDialogOpen(false);
      loadSystems();
    } catch (error) {
      toast.error('Failed to save system');
    }
  };

  const handleDelete = async (system) => {
    if (!window.confirm(`Delete "${system.name}"? This may affect related conditions.`)) return;
    try {
      await adminDeleteSystem(system.id);
      toast.success('System deleted');
      loadSystems();
    } catch (error) {
      toast.error('Failed to delete system');
    }
  };

  if (loading) {
    return <div className="text-stone-400 p-8">Loading systems...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Body Systems</h1>
          <p className="text-stone-600 text-sm">Organize conditions by body system</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-system-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Add System
        </Button>
      </div>

      {systems.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-stone-500 mb-4">No systems created yet</p>
            <Button onClick={openCreateDialog} variant="outline" className="btn-outline">
              Create Your First System
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {systems.map((system) => (
            <Card key={system.id} className="card-hover" data-testid={`system-card-${system.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-[#2C2C2C]">{system.name}</h3>
                    <p className="text-sm text-[#4A6763]">{system.consumer_label}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(system)}
                      className="h-8 w-8 text-stone-400 hover:text-[#4A6763]"
                      data-testid={`edit-system-${system.id}`}
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(system)}
                      className="h-8 w-8 text-stone-400 hover:text-red-500"
                      data-testid={`delete-system-${system.id}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
                {system.description && (
                  <p className="text-sm text-stone-500 line-clamp-2">{system.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingSystem ? 'Edit System' : 'Add System'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="form-label">Internal Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., digestive_system"
                  required
                  data-testid="system-name-input"
                />
              </div>
              <div>
                <label className="form-label">Display Label</label>
                <Input
                  value={formData.consumer_label}
                  onChange={(e) => setFormData({ ...formData, consumer_label: e.target.value })}
                  placeholder="e.g., Digestive System"
                  required
                  data-testid="system-label-input"
                />
              </div>
              <div>
                <label className="form-label">Description (Optional)</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description..."
                  rows={3}
                  data-testid="system-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-system-btn">
                {editingSystem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSystems;
